import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { RefreshCcw } from "lucide-react";
import axiosClient from "../api/axiosClient";
import "../styles/FlaggedTable.css";

export default function BlockedUserTable({ onUserClick }) {
  // ─── Local State ──────────────────────────────────────────────────────────
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState([]);
  const [loadingToggle, setLoadingToggle] = useState(null); // userId currently being toggled

  const pageSize = 10;

  // ─── Fetch Blocked Users ──────────────────────────────────────────────────
  const fetchBlockedUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem("admintoken");

      const res = await axiosClient.get("/auth/admin/blockedUserList", {
        params: {
          page: pageIndex,
          size: pageSize,
          search: search,
        },
        headers: {
          Authorization: `${token}`,
        },
      });

      console.log("Blocked users response:", res.data);

      // Adjust field names below to match your actual API response shape
      setBlockedUsers(res.data.users ?? res.data.content ?? res.data ?? []);
      setTotalPages(res.data.totalPages ?? 1);
    } catch (error) {
      console.error("fetchBlockedUsers error:", error);
    }
  }, [pageIndex, search]);

  useEffect(() => {
    fetchBlockedUsers();
  }, [pageIndex]);

  // ─── Block / Unblock Toggle ───────────────────────────────────────────────
  const toggleBlock = async (userId, isBlocked) => {
    try {
      setLoadingToggle(userId);
      const token = localStorage.getItem("admintoken");

      if (isBlocked) {
        await axiosClient.post(
          `/auth/admin/${userId}/unblockUser`,
          { userId },
          { headers: { Authorization: `${token}` } }
        );
      } else {
        await axiosClient.post(
          `/auth/admin/${userId}/blockUser`,
          { userId },
          { headers: { Authorization: `${token}` } }
        );
      }

      await fetchBlockedUsers();
    } catch (error) {
      console.error("toggleBlock error:", error);
    } finally {
      setLoadingToggle(null);
    }
  };

  // ─── Reset ────────────────────────────────────────────────────────────────
  const handleReset = () => {
    setSearch("");
    setPageIndex(0);
    fetchBlockedUsers();
  };

  // ─── Pagination Window ────────────────────────────────────────────────────
  const visiblePages = 3;
  const start = Math.max(pageIndex - Math.floor(visiblePages / 2), 0);
  const end = Math.min(start + visiblePages, totalPages ?? 0);

  // ─── Columns ──────────────────────────────────────────────────────────────
  const columns = useMemo(
    () => [
      {
        accessorKey: "userId",
        header: "User ID",
        cell: ({ row }) => {
          const userId = row.original.userId;
          return (
            <span
              className="but-userid-cell"
              title={userId}
            >
              {userId?.slice(0, 6)}...
            </span>
          );
        },
      },
      {
        accessorKey: "name",
        header: "Name",
      },
      {
        accessorKey: "email",
        header: "Email",
      },
      {
        header: "Status",
        cell: ({ row }) => {
          const isBlocked = !(row.original.isBlocked ?? row.original.blocked);
          return (
            <span
              className={`but-status-badge ${
                isBlocked ? "but-status-blocked" : "but-status-active"
              }`}
            >
              {isBlocked ? "Blocked" : "Unblocked"}
            </span>
          );
        },
      },
      {
        header: "Action",
        cell: ({ row }) => {
         const isBlocked = !(row.original.isBlocked ?? row.original.blocked);
          const userId = row.original.userId;
          const isLoading = loadingToggle === userId;

          return (
            <div className="but-action-cell">
              <button
                className={`but-toggle-btn ${
                  isBlocked ? "but-btn-unblock" : "but-btn-block"
                }`}
                onClick={() => toggleBlock(userId, isBlocked)}
                disabled={isLoading}
              >
                {isLoading ? "..." : isBlocked ? "Unblock" : "Block"}
              </button>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadingToggle]
  );

  // ─── Table Instance ───────────────────────────────────────────────────────
  const table = useReactTable({
    data: Array.isArray(blockedUsers) ? blockedUsers : [],
    columns,
    state: {
      sorting,
      pagination: { pageIndex, pageSize },
    },
    onSortingChange: setSorting,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: false,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
  });

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="but-wrapper">
      {/* ── Toolbar ── */}
      {/* <div className="but-toolbar">
        <input
          className="but-search-input"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPageIndex(0);
          }}
          placeholder="Search by name, email, or User ID"
        />

        <button
          className="but-search-btn"
          onClick={() => {
            setPageIndex(0);
            fetchBlockedUsers();
          }}
        >
          Search
        </button>

        <button
          className="but-reset-btn"
          title="Reset All Filters"
          onClick={handleReset}
        >
          <RefreshCcw size={16} />
        </button>
      </div> */}

      {/* ── Table ── */}
      <div className="but-table-scroll">
        <table className="but-table">
          <thead className="but-thead">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="but-header-row">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="but-th"
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                    {{
                      asc: " 🔼",
                      desc: " 🔽",
                    }[header.column.getIsSorted()] ?? null}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody className="but-tbody">
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="but-empty-row">
                  No blocked users found.
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="but-body-row">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="but-td">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      <div className="but-pagination">
        <button
          className="but-page-btn"
          disabled={pageIndex === 0}
          onClick={() => setPageIndex((prev) => Math.max(prev - 1, 0))}
        >
          Previous
        </button>

        {totalPages > 0 &&
          [...Array(end - start).keys()].map((i) => {
            const page = start + i;
            return (
              <button
                key={page}
                className={`but-page-btn ${
                  pageIndex === page ? "but-page-active" : ""
                }`}
                onClick={() => setPageIndex(page)}
              >
                {page + 1}
              </button>
            );
          })}

        <button
          className="but-page-btn"
          disabled={pageIndex >= totalPages - 1}
          onClick={() =>
            setPageIndex((prev) => Math.min(prev + 1, totalPages - 1))
          }
        >
          Next
        </button>
      </div>
    </div>
  );
}